import { useCallback, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';

import { Button } from '@/components/ui/button';
import { useAccount } from '@/hooks/useAccount';
import { cn } from '@/lib/utils';
import { DateRangeMenu } from '@/modules/analytics/components/filters/DateFilter/DateRangeMenu';
import { useRetentionBounds } from '@/modules/analytics/components/filters/DateFilter/useRetentionBounds';
import {
  type DateRange,
  type DateSelection,
  periodToSelection,
  resolveDateSelection,
  selectionToPeriod,
} from '@/modules/analytics/dateSelection';
import { Period } from '@/modules/analytics/types';
import { formatDateRangeLabel } from '@/modules/analytics/utils/formatDateRangeLabel';

interface Props {
  selection: DateSelection;
  onSelectionChange: (selection: DateSelection) => void;
  className?: string;
}

/**
 * Date-range control for the Activity Log, modelled on the analytics `DateFilter`
 * but controlled via props (no Redux / URL coupling).
 */
export const ActivityLogDateFilter = ({ selection, onSelectionChange, className }: Props) => {
  const { t } = useTranslation();
  const { account } = useAccount();
  // The hook decides what a missing limit means, so no fallback here.
  const logsLimit = account?.max_log_retention;

  const [dateOpen, setDateOpen] = useState(false);

  const { startDate, endDate } = useMemo(() => resolveDateSelection(selection), [selection]);
  const selectedPeriod = useMemo(() => selectionToPeriod(selection) ?? null, [selection]);

  const menuRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useClickAway(menuRef, (event) => {
    if (calendarRef.current?.contains(event.target as Node)) {
      return;
    }
    setDateOpen(false);
  });

  const { isPresetDisabled, isDayDisabled, earliest, today } = useRetentionBounds(logsLimit);

  const handlePresetSelect = useCallback(
    (period: Period) => {
      const next = periodToSelection(period);
      if (next) {
        onSelectionChange(next);
      }
      setDateOpen(false);
    },
    [onSelectionChange]
  );

  const handleRangeSelect = useCallback(
    (range: DateRange) => {
      onSelectionChange({ type: 'custom', ...range });
      setDateOpen(false);
    },
    [onSelectionChange]
  );

  const triggerLabel = (() => {
    if (startDate && endDate) {
      return formatDateRangeLabel(startDate, endDate);
    }
    const single = startDate || endDate;
    return single ? formatDateRangeLabel(single, single) : t('analytics.periods.custom');
  })();

  return (
    <div className={cn('relative z-30', className)} ref={menuRef}>
      <Button
        variant="outline-neutral"
        size="sm"
        className="leading-6"
        aria-expanded={dateOpen}
        aria-haspopup="dialog"
        onClick={() => setDateOpen((prev) => !prev)}
      >
        {triggerLabel}
      </Button>

      {dateOpen ? (
        <DateRangeMenu
          ref={calendarRef}
          startDate={startDate}
          endDate={endDate}
          onPresetSelect={handlePresetSelect}
          onRangeSelect={handleRangeSelect}
          isPresetDisabled={isPresetDisabled}
          isDayDisabled={isDayDisabled}
          retentionStart={earliest}
          today={today}
          selectedPeriod={selectedPeriod}
          onRequestClose={() => setDateOpen(false)}
        />
      ) : null}
    </div>
  );
};
